/**
 * Vest NQ-PERP price candles drawn over the CME candles (MNQ/NQ charts).
 *
 * PRICES. The engine's 2m Vest bars carry traded NQ-PERP OHLC (`o/h/l/c`,
 * utt-engine vest_scanner/volume.rs), from the tape only: kline / mixed bars
 * have none and are skipped (~1.4% of recorded bars). Vest trades above CME by a basis that
 * drifts (~+25 pts, ~14 pts over a session; Vest's index price == CME), so
 * each bar is shifted onto the CME axis by subtracting a rolling gap.
 *
 * GAP ESTIMATOR. On the native 2m grid: a pair is a 2m bucket closed on BOTH
 * venues with a Vest close, d = Vest close - CME close. The gap of the bar
 * opening at t = median of d over the last GAP_WINDOW (30) pairs with open
 * strictly before t and no older than GAP_MAX_AGE_MS (6h); at least
 * GAP_MIN_SAMPLES (3) pairs or the bar gets no gap and is not drawn.
 * - Strictly earlier: a bar never cancels its own close difference (a per-bar
 *   gap would zero every close), and the forming bar is shifted by the same
 *   gap it keeps once it closes, so nothing repaints at the close.
 * - Median: one bad print or a fast spike does not drag the shift.
 * - 6h cap: across a weekend or a long halt, Friday's basis is not reused.
 * - Computed in the browser from the bars on hand on every update; nothing is
 *   cached or stored (the basis is never valid for long).
 *
 * TIMEFRAMES. Each 2m bar is translated by its own gap, then aggregated to the
 * chart bar exactly like the CME candles (`floor(t / I) * I`). Below 2m each
 * candle shows the 2m bar containing it (as the Vest volume pane does). A
 * chart bar holding a bar with volume but no prices, or no gap, is skipped:
 * never filled in from the VWAP. Points are produced only at the chart's own
 * candle times: an extra time point would join the main time scale and shift
 * every logical index the volume pane is synced by.
 */
import { VestBar } from './vestVolume';
import { candleTailUpdates, cmeClosedCloses, foldPlacedBars, median, type Ohlc, type PlacedBar } from './overlayCandles';

// Shared with the MES overlay (utils/mesCandles); re-exported for existing callers.
export { candleTailUpdates, cmeClosedCloses, median };
export type { Ohlc };

export const GAP_WINDOW = 30;
export const GAP_MIN_SAMPLES = 3;
export const GAP_MAX_AGE_MS = 6 * 60 * 60_000;

/**
 * Outline colours: green up, red down, as pale tints (Tailwind green-200 /
 * red-300). Brighter #4ade80 / #f87171 were the first pick, but #4ade80 IS the
 * Glass Frost theme's candleUp and #f87171 sits a few shades off the Glass
 * Frost / Aurora Glass / One Dark reds, so the outline vanished into the CME
 * body there. These tints stay clear of every theme's --theme-candle-up/down
 * (closest: Catppuccin #a6e3a1 / #f38ba8) while still reading as green vs red.
 */
export const VEST_CANDLE_COLORS = { up: '#bbf7d0', down: '#fca5a5' } as const;

export interface VestGap {
  /** Vest close - CME close, points (positive = Vest above CME). */
  gap: number;
  /** Pairs the median was taken over. */
  samples: number;
}

/** One overlay candle, on the CME axis. */
export interface VestCandle {
  /** Chart bar open, seconds. */
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  /** Gap subtracted from the chart bar's last 2m bar. */
  gap: number;
  /** 2m bars folded in. */
  bars: number;
}

const finite = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);

/** The bar's traded prices, or null unless all four are known. */
export function vestOhlc(b: VestBar): Ohlc | null {
  return finite(b.o) && finite(b.h) && finite(b.l) && finite(b.c) ? [b.o, b.h, b.l, b.c] : null;
}

/**
 * Gap per Vest bar open (ms) for every priced bar that has one (see header).
 * Bars before the first CME bucket can never have a gap and are skipped.
 */
export function rollingGaps(bars: Iterable<VestBar>, cmeCloses: ReadonlyMap<number, number>): Map<number, VestGap> {
  const out = new Map<number, VestGap>();
  if (cmeCloses.size === 0) return out;
  let firstCme = Infinity;
  for (const t of cmeCloses.keys()) firstCme = Math.min(firstCme, t);
  const sorted = [...bars].filter((b) => b.t >= firstCme).sort((a, b) => a.t - b.t);
  const pairs: { t: number; d: number }[] = [];
  for (const b of sorted) {
    const px = vestOhlc(b);
    if (px) {
      const window: number[] = [];
      for (let i = pairs.length - 1; i >= 0 && window.length < GAP_WINDOW; i--) {
        if (pairs[i].t <= b.t - GAP_MAX_AGE_MS) break;
        window.push(pairs[i].d);
      }
      if (window.length >= GAP_MIN_SAMPLES) out.set(b.t, { gap: median(window), samples: window.length });
    }
    const cme = cmeCloses.get(b.t);
    if (px && b.closed && cme !== undefined) pairs.push({ t: b.t, d: px[3] - cme });
  }
  return out;
}

/**
 * Overlay candles at the chart's candle times (seconds, ascending), on the CME
 * axis. `timeframeMinutes` is the chart's.
 */
export function vestCandleData(
  chartTimesSec: readonly number[],
  bars: Iterable<VestBar>,
  gaps: ReadonlyMap<number, VestGap>,
  timeframeMinutes: number,
): VestCandle[] {
  const placed: PlacedBar[] = [];
  for (const b of bars) {
    if (!(b.v > 0)) continue; // no prints: nothing to draw, nothing missing
    const px = vestOhlc(b);
    const g = gaps.get(b.t);
    if (!px || !g) {
      placed.push({ t: b.t, px: null, adj: NaN });
      continue;
    }
    const d = g.gap;
    placed.push({ t: b.t, px: [px[0] - d, px[1] - d, px[2] - d, px[3] - d], adj: d });
  }
  return foldPlacedBars(chartTimesSec, placed, timeframeMinutes).map(({ adj, ...c }) => ({ ...c, gap: adj }));
}

/** The gap applied to the newest drawn candle. */
export function latestGap(data: readonly VestCandle[]): number | null {
  return data.length > 0 ? data[data.length - 1].gap : null;
}

/** "Vest −25.25": the shift applied to Vest prices (minus the gap). */
export function gapLabel(gap: number | null): string {
  if (gap === null || !Number.isFinite(gap)) return 'Vest …';
  const shift = -gap;
  const sign = shift < 0 ? '−' : '+';
  return `Vest ${sign}${Math.abs(shift).toFixed(2)}`;
}
